import { AUTH_VARIANT_PARAM } from '@/utils/pages/auth-variant';
import { setAuthAwareCacheHeaders } from '@/utils/pages/cache-headers';
import { getDehydratedState, prefetchMotionGraphicsSearch } from '@/utils/search/ssr';
import { HydrationBoundary, QueryClient, type DehydratedState } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryState } from '@/hooks/state/useQueryState';
import { useListingInfiniteScrollActive } from '@/hooks/useListingInfiniteScrollActive';
import { useShowMoreVariant } from '@/hooks/useShowMoreVariant';
import FAQs from '@/components/FAQs';
import { MGTile } from '@/components/MGTile';
import { SEO } from '@/components/SEO';
import TypesenseFilters from '@/components/TypesenseFilters';
import { CenterpieceSearch } from '@/features/CenterpieceSearch';
import { LutsTipsBanner } from './LutsTipsBanner';
import { useTypesenseFilterParams } from '@/hooks/useTypesenseFilterParams';
import { useTypesenseSearch } from '@/hooks/useTypesenseSearch';
import { LUTS_FILTER, getSearches, isValidSort, type SortOption } from '@music-vine/setlist';
import { withServerAuth } from '@/lib/auth/withServerAuth';
import { MGProvider } from '@/providers/MGProvider';
import { Separator } from '@music-vine/cadence/ui';
import { lutsFaqContent } from './faq-content';
import type { MotionGraphics } from '@music-vine/setlist';
import { MAX_PAGINATED_PAGES } from '@/utils/pagination';
import type { NextPage } from 'next';
import { useShowFreeFirst } from '@/hooks/search/shared/useShowFreeFirst';
import { useShowFreeFirstAvailable } from '@/hooks/search/shared/useShowFreeFirstAvailable';
import { ASSET_TYPES } from '@/components/TypesenseFilters/assetTypes';
import { FreeFirstMotionGraphicsSections } from '@/components/FreeFirstMotionGraphicsSections';
import { getSectionCopy } from '@/utils/show-free-first/sectionCopy';
export interface LUTsPageProps {
  dehydratedState?: DehydratedState;
  shouldIntersperseFreeAndPremium: boolean;
}
const LUTsPageContent: React.FC<LUTsPageProps> = ({
  shouldIntersperseFreeAndPremium
}) => {
  const router = useRouter();
  const [showFreeFirst] = useShowFreeFirst();
  const freeFirstAvailable = useShowFreeFirstAvailable(ASSET_TYPES.MOTION_GRAPHICS);
  const freeFirstActive = showFreeFirst && freeFirstAvailable;

  // Use useQueryState to read sort parameter consistently with TypesenseSort components
  const [currentSort] = useQueryState('sort', {
    defaultValue: 'relevancy' as SortOption,
    parse: value => isValidSort(value) ? value : 'relevancy',
    serialize: value => value
  });

  // Create preselected filters for LUTs
  const lutsPreselectedFilters = useMemo(() => ({
    is_lut: {
      included: ['true'],
      excluded: []
    }
  }), []);

  // Get combined filters from the jotai-based filter system
  const {
    filter: combinedFilters,
    search: urlSearchTerms
  } = useTypesenseFilterParams();

  // Combine user filters with the LUTs filter
  const filter = combinedFilters ? `${combinedFilters} && ${LUTS_FILTER}` : LUTS_FILTER;

  // Base URL for canonical and pagination links
  const siteUrl = (process.env.NEXT_PUBLIC_SITE_URL ?? 'https://uppbeat.io').replace(/\/$/, '');

  // Handle page parameter changes
  useEffect(() => {
    const queryPage = router.query.page ? Number.parseInt(router.query.page as string, 10) : 1;

    // Validate page parameter
    if (Number.isNaN(queryPage) || queryPage < 1) {
      void router.replace({
        pathname: router.pathname,
        query: {
          ...router.query,
          page: undefined,
          // Spreading router.query would write middleware's cache-key marker
          // into the address bar; a shared copy of that URL forces `no-store`
          // for everyone who opens it.
          [AUTH_VARIANT_PARAM]: undefined
        }
      });
    }
  }, [router]);

  // Handle search terms array conversion
  const searchTermsArray = Array.isArray(urlSearchTerms) ? urlSearchTerms : urlSearchTerms ? [urlSearchTerms] : [];
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useTypesenseSearch<MotionGraphics>({
    searches: getSearches({
      q: searchTermsArray && searchTermsArray.length > 0 ? searchTermsArray.join(', ') : undefined,
      filter_by: filter,
      // Combined user filters with LUTs filter
      sort: currentSort,
      fetchFreeAndPremium: shouldIntersperseFreeAndPremium,
      isHomePage: true
    }),
    cacheOptions: {
      cacheSearchResultsForSeconds: 60
    },
    shouldIntersperseFreeAndPremium,
    keepPrevious: true // Show previous results while new ones load
  });
  const showMoreVariant = useShowMoreVariant();
  const [isInfiniteStreamActive, setIsInfiniteStreamActive] = useState(false);
  const [hasShownMore, setHasShownMore] = useState(false);
  const handleShowMore = useCallback(() => setHasShownMore(true), []);

  // Group B: hide the site footer while the listing streams more results
  // and reveal it at the true end. Group A keeps the footer as today.
  useListingInfiniteScrollActive(showMoreVariant === 'B' && isInfiniteStreamActive);
  const tiles = useMemo(() => data?.results?.map(mg => {
    if (!(mg && 'document' in mg)) {
      return null;
    }
    return <MGTile compatibility={mg.document.compatibility} fileSize={mg.document.file_size} hideCompatibility id={mg.document.id} image={mg.document.image} isLut key={`mg-tile-${mg.document.id}`} meta={{
      contributor: mg.document.contributor.name,
      setLiveUnixTimestamp: mg.document.set_live ?? 0,
      resolution: mg.document.resolution,
      isPremium: mg.document.is_premium,
      compatibility: mg.document.compatibility,
      onAlpha: mg.document.on_alpha,
      relevanceFree: mg.document.relevance_free,
      relevancePremium: mg.document.relevance_premium,
      isFeatured: mg.document.is_featured,
      downloadCountFree: mg.document.free_download_count,
      playCountFree: mg.document.free_play_count,
      downloadCountPremium: mg.document.premium_download_count,
      playCountPremium: mg.document.premium_play_count
    }} mgSlug={mg.document.slug} title={mg.document.name} video={mg.document.preview_video_url} />;
  }), [data]);
  const currentPage = router.query.page ? Number.parseInt(router.query.page as string, 10) : 1;

  // Past the cap the paginated route 404s.
  const showMoreHref = currentPage < MAX_PAGINATED_PAGES ? `/luts/page/${currentPage + 1}` : undefined;
  return <div data-sentry-component="LUTsPageContent" data-sentry-source-file="index.page.tsx">
      <SEO canonical={currentPage > 1 ? `${siteUrl}/luts?page=${currentPage}` : `${siteUrl}/luts`} description="Transform your videos with free to download LUTs (Look-Up Tables) and color grading presets. Enhance your footage with cinematic looks and professional color treatments!" title="Free LUTs & Color Grading Presets • Uppbeat" data-sentry-element="SEO" data-sentry-source-file="index.page.tsx">
        {currentPage > 1 && <link href={`${siteUrl}/luts${currentPage > 2 ? `?page=${currentPage - 1}` : ''}`} rel="prev" />}
        {!!hasNextPage && <link href={`${siteUrl}/luts?page=${currentPage + 1}`} rel="next" />}
      </SEO>
      <div>
        <CenterpieceSearch variant="luts" data-sentry-element="CenterpieceSearch" data-sentry-source-file="index.page.tsx" />
        <Separator className="mx-container mb-5 w-auto" data-sentry-element="Separator" data-sentry-source-file="index.page.tsx" />
        <TypesenseFilters facets={[{
        value: 'styles.id',
        label: 'Style',
        display: 'popover'
      }]} hidePrimaryToggles lutsPage isSticky={true} stickyContainerClassName="top-[51px] md:top-[67px]"
      // Pre-apply the LUTs filter with the correct structure
      preSelectedFilters={lutsPreselectedFilters} data-sentry-element="TypesenseFilters" data-sentry-source-file="index.page.tsx">
          {freeFirstActive ? <FreeFirstMotionGraphicsSections assetType="lut" filter={filter} isLut premiumHeading={getSectionCopy({
          assetType: 'lut',
          pageType: 'other'
        }).premiumHeading} q={urlSearchTerms ? urlSearchTerms.join(' ') : undefined} sort={currentSort} /> : <MGProvider comprehensiveListingsVariant="luts" fetchNextPage={fetchNextPage} hasNextPage={hasNextPage} isFetchingNextPage={isFetchingNextPage} noResults={data?.results?.length === 0 && !isFetchingNextPage} onInfiniteScrollEnabled={handleShowMore} onInfiniteStreamChange={setIsInfiniteStreamActive} showComprehensiveListings showMoreHref={showMoreHref}>
              {tiles}
            </MGProvider>}
        </TypesenseFilters>

        {hasShownMore ? null : <div className="px-container">
            <Separator className="mt-10" />
            <FAQs faqs={lutsFaqContent} variant="inline" />
            <div className="mb-10">
              <LutsTipsBanner />
            </div>
          </div>}
      </div>
    </div>;
};
const LUTsPage: NextPage<LUTsPageProps> = ({
  dehydratedState,
  ...props
}) => <HydrationBoundary state={dehydratedState} data-sentry-element="HydrationBoundary" data-sentry-component="LUTsPage" data-sentry-source-file="index.page.tsx">
    <LUTsPageContent {...props} data-sentry-element="LUTsPageContent" data-sentry-source-file="index.page.tsx" />
  </HydrationBoundary>;
export const getServerSideProps = withServerAuth<LUTsPageProps>(async (auth, context) => {
  // If not authenticated OR has 'Free' role, show interspersed results
  const shouldIntersperseFreeAndPremium = !auth.isAuthenticated || auth.hasRole('Free');

  // Extract page parameter from URL for pagination support
  const pageParam = context.query.page;
  const currentPage = typeof pageParam === 'string' ? Number.parseInt(pageParam, 10) : 1;
  const validPage = !Number.isNaN(currentPage) && currentPage > 0 ? currentPage : 1;

  // The client reads `sort` straight off the URL, so the prefetch has to as
  // well — otherwise a sorted URL prefetches under the relevancy key the
  // grid never asks for, and pays for the search twice (DEV-6490).
  const sortParam = context.query.sort;
  const normalizedSort = isValidSort(sortParam) ? sortParam : 'relevancy';
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: Number.POSITIVE_INFINITY
      }
    }
  });
  try {
    // Prefetch search results for hydration with LUTs filter
    // Pass page parameter to ensure server-rendered content matches the requested page
    const prefetchResult = await prefetchMotionGraphicsSearch({
      queryClient,
      categorySlug: '',
      // Homepage has no category slug
      preselectedFilters: LUTS_FILTER,
      fetchFreeAndPremium: shouldIntersperseFreeAndPremium,
      isHomePage: true,
      initialSort: normalizedSort,
      page: validPage
    });

    // If totalCount is undefined, the page number is invalid (exceeds max or has no results)
    // Return 404 for invalid pagination URLs
    if (prefetchResult?.totalCount === undefined) {
      return {
        notFound: true
      };
    }
  } catch {
    // Continue without prefetch - page will load but client will fetch data
  }
  setAuthAwareCacheHeaders(context, {
    isAuthenticated: auth.isAuthenticated
  });
  return {
    props: {
      shouldIntersperseFreeAndPremium,
      dehydratedState: getDehydratedState(queryClient)
    }
  };
});
export default LUTsPage;